import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './TextImage.module.css';
import image1 from "../../images/טלי אטיה לפני אחרי מיוחד.webp"
import image2 from "../../images/טלי אטיה לפני אחרי מיוחד 2.webp"
import image3 from "../../images/טלי אטיה לפני אחרי מיוחד 3.webp"

gsap.registerPlugin(ScrollTrigger);

const LuxurySection = () => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const wordsRef = useRef([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageTransitionRef = useRef(null);

  // מערך התמונות
  const images = [image1, image2, image3];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // אנימציה מיוחדת לכל מילה
      wordsRef.current.forEach((word, index) => {
        if (word) {
          gsap.fromTo(
            word,
            {
              opacity: 0,
              y: 50,
              rotationX: -90,
              scale: 0.5,
            },
            {
              opacity: 1,
              y: 0,
              rotationX: 0,
              scale: 1,
              duration: 1.2,
              delay: index * 0.15,
              ease: "back.out(1.7)",
              scrollTrigger: {
                trigger: containerRef.current,
                start: "top 75%",
                end: "top 25%",
                toggleActions: "play none none reverse",
                once: false,
              },
            }
          );
        }
      });

      // אנימציית חשיפה מימין לשמאל לתמונה
      if (imageRef.current) {
        gsap.fromTo(
          imageRef.current,
          {
            clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
            opacity: 0,
          },
          {
            clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
            opacity: 1,
            duration: 1.5,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top 70%",
              end: "top 30%",
              toggleActions: "play none none reverse",
              once: false,
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // אוטומטי מעבר תמונות
  useEffect(() => {
    const interval = setInterval(() => {
      changeImage((currentImageIndex + 1) % images.length);
    }, 4000); // מחליף תמונה כל 4 שניות

    return () => clearInterval(interval);
  }, [currentImageIndex]);

  const changeImage = (newIndex) => {
    if (imageTransitionRef.current) {
      // אנימציית סגירה - מסתיר את התמונה הנוכחית
      gsap.to(imageTransitionRef.current, {
        clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
        duration: 0.8,
        ease: "power3.inOut",
        onComplete: () => {
          // משנה את התמונה
          setCurrentImageIndex(newIndex);
          
          // אנימציית פתיחה - מציג את התמונה החדשה
          gsap.fromTo(
            imageTransitionRef.current,
            {
              clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
            },
            {
              clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
              duration: 1.2,
              ease: "power3.inOut",
            }
          );
        },
      });
    }
  };

  const line1 = ["Transform", "your", "space,"];
  const line2 = ["Transform", "your", "Life"];

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.content}>
        {/* טקסט בצד שמאל */}
        <div className={styles.textSection} ref={textRef}>
          <h1 className={styles.heading}>
            <div className={styles.line}>
              {line1.map((word, index) => (
                <span
                  key={`line1-${index}`}
                  className={`${styles.word} ${index === 2 ? styles.emphasis : ''}`}
                  ref={(el) => {
                    if (el) wordsRef.current[index] = el;
                  }}
                >
                  {word}
                  {index < line1.length - 1 ? " " : ""}
                </span>
              ))}
            </div>
            <div className={styles.line}>
              {line2.map((word, index) => (
                <span
                  key={`line2-${index}`}
                  className={`${styles.word} ${index === 2 ? styles.emphasis : ''}`}
                  ref={(el) => {
                    if (el) wordsRef.current[line1.length + index] = el;
                  }}
                >
                  {word}
                  {index < line2.length - 1 ? " " : ""}
                </span>
              ))}
            </div>
          </h1>
        </div>

        {/* קרוסלת תמונות בצד ימין */}
        <div className={styles.imageSection}>
          <div className={styles.imageWrapper} ref={imageRef}>
            <div className={styles.glassOverlay}></div>
            <img
              ref={imageTransitionRef}
              src={images[currentImageIndex]}
              alt="Luxury Interior"
              className={styles.image}
              loading="lazy"
            />
            
            {/* נקודות ניווט */}
            <div className={styles.dotsContainer}>
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.dot} ${index === currentImageIndex ? styles.activeDot : ''}`}
                  onClick={() => changeImage(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
            
            {/* חיצים לניווט ידני */}
            <button
              className={`${styles.navButton} ${styles.prevButton}`}
              onClick={() => changeImage((currentImageIndex - 1 + images.length) % images.length)}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              className={`${styles.navButton} ${styles.nextButton}`}
              onClick={() => changeImage((currentImageIndex + 1) % images.length)}
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LuxurySection;